import { useCallback, useEffect, useRef, useState } from "react";
import { phaseLabel, type JourneyProgress } from "../../core/rider-journey";
import type { RiderPhase } from "../../shared/types";
import { postHaptic } from "../native";
import { HOLD_DESTRUCTIVE_MS, HOLD_MS } from "./HoldButton";

/**
 * Long enough that a knuckle brushing the screen in a coat pocket does nothing, and long enough
 * that the fill has something to say. A tap deliberately does not move the journey.
 *
 * In a browser the fill is the whole acknowledgement, because iOS Safari has no Vibration API and
 * the switch-control trick that is supposed to stand in for one produced nothing on the phone this
 * is built for. Inside the iOS shell the buzz is real; everywhere else it is silently nothing.
 *
 * The two durations are `HoldButton`'s, so that every hold in the app is the same hold.
 */
const RESET_HOLD_MS = HOLD_DESTRUCTIVE_MS;

/**
 * Past this the finger is going somewhere rather than pressing.
 *
 * Dragging sideways scrolls the row, and the browser cancels the press itself once it decides
 * that is what is happening — but only once it has decided, and a mouse never scrolls at all.
 */
const MOVE_CANCEL_PX = 8;

/**
 * Grace before the hold begins, so a finger setting off sideways never lights the pill.
 *
 * Short enough not to feel unresponsive, long enough that a drag has already declared itself.
 */
const HOLD_START_DELAY_MS = 140;

/** Close enough to the start that Reset grabs the edge while the first phase sits to its right. */
const RESET_CAPTURE_PX = 96;

/** How long the rider gets to look down the track before it slides back to where they are. */
const SNAP_BACK_MS = 1500;

/** Our own scrolling must not read as the rider looking around. */
const PROGRAMMATIC_SCROLL_MS = 600;
/** Reset should visibly return to the current phase without using the browser's leisurely scroll. */
const QUICK_CENTRE_MS = 180;

/**
 * Where the rider is, with the rest of the morning either side of it.
 *
 * The whole track is here rather than three phases: being able to look ahead is worth more than
 * the tidiness of a fixed row. It stays centred on where the rider is, and after a drag it
 * comes back on its own rather than leaving them somewhere they did not mean to be.
 */
export function ProgressLine({
	progress,
	phases,
	hasAccessLeg,
	onDeclare,
	onReset,
}: {
	progress: JourneyProgress;
	/** This journey's track. The ride home has no morning to plan and no drive to make. */
	phases: RiderPhase[];
	hasAccessLeg: boolean;
	onDeclare: (phase: RiderPhase) => void;
	onReset: () => void;
}) {
	const trackRef = useRef<HTMLDivElement>(null);
	const currentRef = useRef<HTMLElement | null>(null);
	const snapTimer = useRef<number | null>(null);
	const centreAnimation = useRef<number | null>(null);
	const programmaticUntil = useRef(0);
	const pendingCentre = useRef<number | null>(null);
	// A phase declared under a finger owes a slide, and it is a different debt from the scroll
	// snap-back: sharing one slot let a stray scroll turn "the moment you let go" into a second
	// later, which reads as not happening at all.
	const centreOwed = useRef(false);
	// A finger still on the row is still reading it, so nothing may slide out from under it.
	const [touching, setTouching] = useState<null | { onControl: boolean }>(null);
	const touchingRef = useRef(touching);
	touchingRef.current = touching;
	// One reset per touch: after it fires the finger is usually still down, and the row is still
	// open under it, which would otherwise arm the whole thing again.
	const resetSpent = useRef(false);
	const [resetExposed, setResetExposed] = useState(false);
	const resetExposedRef = useRef(false);
	const [resetArming, setResetArming] = useState(false);
	const resetArmingRef = useRef(false);
	const resetTimer = useRef<number | null>(null);
	// Any phase can be declared — he is standing there and the app is not — but the two either
	// side are the ones he means nine times out of ten, so only those are lit.
	const here = phases.indexOf(progress.phase);
	const adjacent = new Set([phases[here - 1], phases[here + 1]].filter(Boolean));

	const centre = useCallback((behavior: ScrollBehavior | "quick") => {
		const track = trackRef.current;
		const pill = currentRef.current;
		if (!track || !pill) {
			return;
		}
		if (centreAnimation.current != null) {
			window.cancelAnimationFrame(centreAnimation.current);
			centreAnimation.current = null;
		}
		const left = pill.offsetLeft - (track.clientWidth - pill.clientWidth) / 2;
		if (behavior === "quick") {
			const from = track.scrollLeft;
			const distance = left - from;
			const startedAt = performance.now();
			programmaticUntil.current = Date.now() + QUICK_CENTRE_MS;
			const animate = (now: number) => {
				const elapsed = Math.min(1, (now - startedAt) / QUICK_CENTRE_MS);
				const eased = 1 - Math.pow(1 - elapsed, 3);
				track.scrollTo({ left: from + distance * eased, behavior: "auto" });
				if (elapsed < 1) {
					centreAnimation.current = window.requestAnimationFrame(animate);
				} else {
					centreAnimation.current = null;
				}
			};
			centreAnimation.current = window.requestAnimationFrame(animate);
			return;
		}
		programmaticUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
		track.scrollTo({ left, behavior });
	}, []);

	const cancelReset = useCallback(() => {
		if (resetTimer.current != null) {
			window.clearTimeout(resetTimer.current);
			resetTimer.current = null;
		}
		resetArmingRef.current = false;
		setResetArming(false);
	}, []);

	const armReset = useCallback(() => {
		if (resetArmingRef.current || resetSpent.current) {
			return;
		}
		resetArmingRef.current = true;
		setResetArming(true);
		resetTimer.current = window.setTimeout(() => {
			resetTimer.current = null;
			resetSpent.current = true;
			resetArmingRef.current = false;
			setResetArming(false);
			postHaptic("warning");
			onReset();
		}, RESET_HOLD_MS);
	}, [onReset]);

	useEffect(() => {
		// The phase moving is a good reason to slide, but not out from under a finger that is
		// still holding the row open — which is exactly where a reset leaves it.
		if (touchingRef.current) {
			centreOwed.current = true;
			return;
		}
		centre("smooth");
	}, [progress.phase, centre]);

	/*
	 * Say it again on the way back in.
	 *
	 * Most of this journey advances itself while the phone is in a pocket — reaching the station,
	 * the tube — so the slide that answers it happens where nobody is looking, against a layout the
	 * browser may not have kept current. Coming back to the app is the one moment worth repeating
	 * oneself, the same reason `useNativeJourney` re-posts the journey then.
	 */
	useEffect(() => {
		const resume = () => {
			if (document.visibilityState === "visible" && !touchingRef.current) {
				centre("quick");
			}
		};
		document.addEventListener("visibilitychange", resume);
		return () => document.removeEventListener("visibilitychange", resume);
	}, [centre]);

	useEffect(
		() => () => {
			if (snapTimer.current != null) {
				window.clearTimeout(snapTimer.current);
			}
			if (centreAnimation.current != null) {
				window.cancelAnimationFrame(centreAnimation.current);
			}
			if (resetTimer.current != null) {
				window.clearTimeout(resetTimer.current);
			}
		},
		[],
	);

	const onScroll = useCallback(() => {
		const track = trackRef.current;
		if (!track) {
			return;
		}
		const exposed = track.scrollLeft <= RESET_CAPTURE_PX;
		resetExposedRef.current = exposed;
		setResetExposed(exposed);
		let touch = touchingRef.current;
		if (touch?.onControl) {
			touch = { onControl: false };
			touchingRef.current = touch;
			setTouching(touch);
		}
		if (exposed && touch) {
			armReset();
		} else if (!exposed) {
			cancelReset();
		}
		if (Date.now() < programmaticUntil.current) {
			return;
		}
		if (snapTimer.current != null) {
			window.clearTimeout(snapTimer.current);
		}
		snapTimer.current = window.setTimeout(() => {
			// Still being held: come back when the finger lifts, not out from under it.
			if (touchingRef.current) {
				pendingCentre.current = SNAP_BACK_MS;
				return;
			}
			centre("smooth");
		}, SNAP_BACK_MS);
	}, [armReset, cancelReset, centre]);

	const beginTouch = useCallback(
		(target: EventTarget | null) => {
			// Once Reset is visible, touching anywhere on the ribbon is holding it open. In
			// particular, a touch used to stop momentum often lands on Planning and produces no
			// further scroll event, so waiting for movement leaves a visible Reset inert.
			const resetHasPriority = resetExposedRef.current;
			const started = {
				onControl: !resetHasPriority && (target as HTMLElement | null)?.closest?.("button.progress__slot") != null,
			};
			touchingRef.current = started;
			setTouching(started);
			if (resetHasPriority) {
				armReset();
			}
		},
		[armReset],
	);

	useEffect(() => {
		if (!touching) {
			return;
		}
		const end = () => {
			const abortedReset = resetArmingRef.current && !resetSpent.current;
			touchingRef.current = null;
			setTouching(null);
			if (!abortedReset) {
				return;
			}
			cancelReset();
			pendingCentre.current = null;
			if (snapTimer.current != null) {
				window.clearTimeout(snapTimer.current);
				snapTimer.current = null;
			}
			centre("quick");
		};
		window.addEventListener("touchend", end);
		window.addEventListener("touchcancel", end);
		window.addEventListener("mouseup", end);
		return () => {
			window.removeEventListener("touchend", end);
			window.removeEventListener("touchcancel", end);
			window.removeEventListener("mouseup", end);
		};
	}, [touching, cancelReset, centre]);

	// Let go, and the deferred slide back happens then rather than never.
	useEffect(() => {
		if (touching) {
			return;
		}
		const resetCompleted = resetSpent.current;
		resetSpent.current = false;
		if (centreOwed.current || resetCompleted) {
			centreOwed.current = false;
			pendingCentre.current = null;
			if (snapTimer.current != null) {
				window.clearTimeout(snapTimer.current);
				snapTimer.current = null;
			}
			centre(resetCompleted ? "quick" : "smooth");
			return;
		}
		const delay = pendingCentre.current;
		if (delay == null) {
			return;
		}
		pendingCentre.current = null;
		const timer = window.setTimeout(() => centre("smooth"), delay);
		return () => window.clearTimeout(timer);
	}, [touching, centre]);

	/*
	 * A mouse has no way to push a row sideways, so dragging one does it by hand. Touch is left
	 * alone: the browser already scrolls it, and doing both would fight.
	 */
	const drag = useRef<{ x: number; from: number } | null>(null);
	const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.pointerType !== "mouse" || !trackRef.current) {
			return;
		}
		drag.current = { x: event.clientX, from: trackRef.current.scrollLeft };
	};
	const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!drag.current || !trackRef.current) {
			return;
		}
		trackRef.current.scrollLeft = drag.current.from - (event.clientX - drag.current.x);
	};
	const endDrag = () => {
		drag.current = null;
	};

	return (
		<div
			className="progress"
			onTouchStart={(event) => beginTouch(event.target)}
			onMouseDown={(event) => beginTouch(event.target)}
		>
			<ResetPill holding={resetArming} visible={resetExposed} overlay />
			<div
				className="progress__track"
				ref={trackRef}
				onScroll={onScroll}
				onPointerDown={startDrag}
				onPointerMove={moveDrag}
				onPointerUp={endDrag}
				onPointerLeave={endDrag}
				onPointerCancel={endDrag}
			>
				<span className="progress__spacer" aria-hidden="true" />
				{/* Keeps the first phase near the right edge while the visible Reset pins to the left. */}
				<ResetPill placeholder />
				{phases.map((phase) =>
					phase === progress.phase ? (
						<span
							key={phase}
							ref={(node) => {
								currentRef.current = node;
							}}
							className="progress__slot"
						>
							<Pill phase={phase} hasAccessLeg={hasAccessLeg} current />
						</span>
					) : (
						<HoldStep
							key={phase}
							phase={phase}
							hasAccessLeg={hasAccessLeg}
							dimmed={!adjacent.has(phase)}
							blocked={resetExposed}
							onCommit={onDeclare}
						/>
					),
				)}
				<span className="progress__spacer" aria-hidden="true" />
			</div>
		</div>
	);
}

/** The visible capsule. Its slot is bigger than it is, so a thumb has more to aim at. */
function Pill({ phase, hasAccessLeg, current = false }: { phase: RiderPhase; hasAccessLeg: boolean; current?: boolean }) {
	return (
		<span className={`progress__pill ${current ? "is-current" : ""}`} data-phase={phase}>
			<span className="progress__fill" aria-hidden="true" />
			<span className="progress__dot" aria-hidden="true" />
			<span className="progress__label">{phaseLabel(phase, hasAccessLeg)}</span>
		</span>
	);
}

/** The press-and-hold, shared by the steps and the reset. */
function useHold(onCommit: () => void) {
	const [holding, setHolding] = useState(false);
	const startTimer = useRef<number | null>(null);
	const timer = useRef<number | null>(null);
	const origin = useRef<{ x: number; y: number } | null>(null);

	const release = useCallback(() => {
		for (const ref of [startTimer, timer]) {
			if (ref.current != null) {
				window.clearTimeout(ref.current);
				ref.current = null;
			}
		}
		origin.current = null;
		setHolding(false);
	}, []);

	useEffect(() => release, [release]);

	const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
		release();
		origin.current = { x: event.clientX, y: event.clientY };
		startTimer.current = window.setTimeout(() => {
			startTimer.current = null;
			setHolding(true);
			timer.current = window.setTimeout(() => {
				release();
				// The double knock of a warning, which reads as a thing having happened rather than
				// a control having been touched. The same feel the reset uses, deliberately.
				postHaptic("warning");
				onCommit();
			}, HOLD_MS);
		}, HOLD_START_DELAY_MS);
	};

	const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
		const start = origin.current;
		if (!start) {
			return;
		}
		if (Math.abs(event.clientX - start.x) > MOVE_CANCEL_PX || Math.abs(event.clientY - start.y) > MOVE_CANCEL_PX) {
			release();
		}
	};

	return {
		holding,
		cancel: release,
		handlers: {
			onPointerDown,
			onPointerMove,
			onPointerUp: release,
			onPointerLeave: release,
			onPointerCancel: release,
			// A long press on iOS otherwise raises the selection callout over the control.
			onContextMenu: (event: React.MouseEvent) => event.preventDefault(),
		},
	};
}

function HoldStep({
	phase,
	hasAccessLeg,
	dimmed = false,
	blocked = false,
	onCommit,
}: {
	phase: RiderPhase;
	hasAccessLeg: boolean;
	/** Further down the track than the next step. Still pressable, just not shouting about it. */
	dimmed?: boolean;
	/** A visible Reset owns the hold gesture until the ribbon recentres. */
	blocked?: boolean;
	onCommit: (phase: RiderPhase) => void;
}) {
	const { holding, handlers, cancel } = useHold(() => onCommit(phase));
	useEffect(() => {
		if (blocked) {
			cancel();
		}
	}, [blocked, cancel]);

	return (
		<button
			type="button"
			className={`progress__slot ${dimmed ? "is-dim" : ""} ${holding ? "is-holding" : ""}`}
			aria-label={`Hold to move to ${phaseLabel(phase, hasAccessLeg)}`}
			aria-disabled={blocked || undefined}
			{...(blocked ? {} : handlers)}
		>
			<Pill phase={phase} hasAccessLeg={hasAccessLeg} />
		</button>
	);
}

/**
 * The way out of a wrong press, sitting before the morning starts.
 *
 * No outline, because it is not a phase — it hands the journey back to the phone rather than
 * moving it anywhere the rider chose. Nothing to press either: holding the row open on it is
 * the whole gesture, and the underline fills instead of the pill for the same reason.
 */
function ResetPill({ holding = false, visible = false, overlay = false, placeholder = false }: { holding?: boolean; visible?: boolean; overlay?: boolean; placeholder?: boolean }) {
	return (
		<span
			className={`progress__slot progress__slot--reset ${overlay ? "is-overlay" : ""} ${visible ? "is-visible" : ""} ${holding ? "is-holding" : ""}`}
			aria-hidden={placeholder || undefined}
		>
			<span className="progress__reset">
				<span className="progress__label">Reset</span>
				<span className="progress__underline" aria-hidden="true" />
			</span>
		</span>
	);
}
